import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import MotionSection from '../components/ui/MotionSection';
import Spotlight from '../components/ui/Spotlight';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiX, FiZap, FiUsers, FiCrown } = FiIcons;

const Pricing = () => {
  const { user, plan, upgradePlan } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');

  const handleStripeCheckout = (planType) => {
    // Simulate Stripe checkout
    alert(`Redirecting to Stripe checkout for ${planType} plan...`);
    // In real implementation, redirect to Stripe checkout
    // window.location.href = 'https://checkout.stripe.com/...';
  };

  const handlePayPalCheckout = (planType) => {
    // For Pro plan, use specific PayPal links based on billing cycle
    if (planType === 'pro' && billingCycle === 'monthly') {
      window.open('https://www.paypal.com/ncp/payment/B8NEHWE65KL2W', '_blank');
    } else if (planType === 'pro' && billingCycle === 'yearly') {
      window.open('https://www.paypal.com/ncp/payment/R4NVJ35HPG9KW', '_blank');
    } 
    // For Team plan, use specific PayPal links based on billing cycle
    else if (planType === 'team' && billingCycle === 'monthly') {
      window.open('https://www.paypal.com/ncp/payment/45QJHC9ZH3ENG', '_blank');
    } else if (planType === 'team' && billingCycle === 'yearly') {
      window.open('https://www.paypal.com/ncp/payment/HD4UXVUZ53MFU', '_blank');
    } else {
      // Simulate PayPal checkout for other plans
      alert(`Redirecting to PayPal checkout for ${planType} plan...`);
    }
  };

  const handleFreePlanAction = () => {
    // If user is not logged in, redirect to signup
    if (!user) {
      window.location.href = '/signup';
      return;
    }
    
    // If user is logged in and on free plan, go to app
    if (plan === 'free') {
      window.location.href = '/app';
      return;
    }
    
    // If user is on a paid plan, show downgrade option
    if (plan === 'pro' || plan === 'team') {
      const confirmDowngrade = confirm('Are you sure you want to downgrade to the Free plan? You will lose access to premium features.');
      if (confirmDowngrade) {
        upgradePlan('free');
        alert('Successfully downgraded to Free plan');
      }
    }
  };

  const plans = [
    {
      name: 'Free',
      price: { monthly: 0, yearly: 0 },
      description: 'Perfect for trying out ColdMail AI',
      icon: FiZap,
      features: [
        '5 generations per day',
        '3 email variants',
        'Save up to 20 emails',
        'Basic templates',
        'Email support'
      ],
      limitations: [
        'No PDF export',
        'No A/B testing',
        'No project management'
      ],
      cta: 'Start Free',
      popular: false,
      current: !user || plan === 'free'
    },
    {
      name: 'Pro',
      price: { monthly: 12, yearly: 120 },
      description: 'For serious sales professionals',
      icon: FiCrown,
      features: [
        'Unlimited generations*',
        'Unlimited A/B variants',
        'PDF & CSV export',
        'Project management',
        'Email history',
        'Priority support',
        'Advanced personalization',
        'Custom templates'
      ],
      limitations: [],
      cta: 'Go Pro',
      popular: true,
      current: user && plan === 'pro'
    },
    {
      name: 'Team',
      price: { monthly: 29, yearly: 290 },
      description: 'For sales teams and agencies',
      icon: FiUsers,
      features: [
        'Everything in Pro',
        '3 team seats',
        'Shared projects',
        'Brand templates',
        'Team analytics',
        'Admin controls',
        'Dedicated support',
        'Custom integrations'
      ],
      limitations: [],
      cta: 'Start Team Trial',
      popular: false,
      current: user && plan === 'team'
    }
  ];

  const faqs = [
    {
      question: 'What does "unlimited generations*" mean?',
      answer: 'Fair usage policy applies. You can generate thousands of emails per month, but we reserve the right to limit excessive usage that impacts our service.'
    },
    {
      question: 'Can I change plans anytime?',
      answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately and billing is prorated.'
    },
    {
      question: 'Is there a refund policy?',
      answer: 'We offer a 30-day money-back guarantee. If you\'re not satisfied, contact us for a full refund.'
    },
    {
      question: 'Do you offer discounts for annual billing?',
      answer: 'Yes! Annual billing saves you 2 months compared to monthly billing. That\'s a 17% discount.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards via Stripe and PayPal. All payments are secure and encrypted.'
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Absolutely! No long-term commitments. You can cancel your subscription anytime from your dashboard.'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-20"
    >
      {/* Hero */}
      <MotionSection className="relative py-20 overflow-hidden">
        <Spotlight size={1000} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Simple, <span className="gradient-text">transparent</span> pricing
          </h1>
          <p className="text-xl text-light/80 mb-8 max-w-3xl mx-auto">
            Choose the plan that fits your needs. Upgrade or downgrade at any time.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4 mb-12">
            <span className={billingCycle === 'monthly' ? 'text-primary' : 'text-light/60'}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className="relative w-14 h-8 bg-dark border border-primary/20 rounded-full transition-colors"
            >
              <div className={`absolute top-1 w-6 h-6 bg-primary rounded-full transition-transform ${
                billingCycle === 'yearly' ? 'translate-x-7' : 'translate-x-1'
              }`} />
            </button>
            <span className={billingCycle === 'yearly' ? 'text-primary' : 'text-light/60'}>
              Yearly <span className="text-sm text-primary">(Save 17%)</span>
            </span>
          </div>
        </div>
      </MotionSection>

      {/* Pricing Cards */}
      <MotionSection className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((planItem, index) => (
              <motion.div
                key={planItem.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative ${
                  planItem.popular 
                    ? 'gradient-border scale-105' 
                    : 'border border-primary/20'
                } rounded-xl p-8 hover:scale-105 transition-transform duration-300`}
              >
                {planItem.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-primary to-secondary text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className={planItem.popular ? 'gradient-border-inner p-8' : 'p-0'}>
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                      <SafeIcon icon={planItem.icon} className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{planItem.name}</h3>
                    <p className="text-light/60 mb-4">{planItem.description}</p>
                    <div className="mb-4">
                      <span className="text-4xl font-bold">
                        ${billingCycle === 'monthly' ? planItem.price.monthly : planItem.price.yearly}
                      </span>
                      <span className="text-light/60">
                        /{billingCycle === 'monthly' ? 'month' : 'year'}
                      </span>
                    </div>
                    {billingCycle === 'yearly' && planItem.price.yearly > 0 && (
                      <p className="text-sm text-primary">
                        ${(planItem.price.monthly * 12 - planItem.price.yearly)} saved annually
                      </p>
                    )}
                  </div>

                  <div className="space-y-4 mb-8">
                    {planItem.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                    {planItem.limitations.map((limitation, idx) => (
                      <div key={idx} className="flex items-center space-x-3 opacity-50">
                        <SafeIcon icon={FiX} className="w-5 h-5 text-light/40 flex-shrink-0" />
                        <span className="text-sm line-through">{limitation}</span>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-3">
                    {planItem.current ? (
                      <div className="w-full py-3 bg-primary/20 text-primary rounded-lg font-medium text-center">
                        {planItem.name === 'Free' ? (
                          <button 
                            onClick={handleFreePlanAction}
                            className="w-full h-full bg-transparent border-none text-primary font-medium cursor-pointer"
                          >
                            Current Plan - Go to App
                          </button>
                        ) : (
                          'Current Plan'
                        )}
                      </div>
                    ) : (
                      <>
                        {planItem.name === 'Free' ? (
                          <button
                            onClick={handleFreePlanAction}
                            className="w-full py-3 border border-primary text-primary rounded-lg font-medium hover:bg-primary hover:text-white transition-colors"
                          >
                            {!user ? 'Start Free' : 'Switch to Free'}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStripeCheckout(planItem.name.toLowerCase())}
                            className={`w-full py-3 rounded-lg font-medium transition-colors ${
                              planItem.popular
                                ? 'btn-primary text-white'
                                : 'border border-primary text-primary hover:bg-primary hover:text-white'
                            }`}
                          >
                            {planItem.cta}
                          </button>
                        )}
                        
                        {planItem.name !== 'Free' && (
                          <button
                            onClick={() => handlePayPalCheckout(planItem.name.toLowerCase())}
                            className="w-full py-2 text-sm text-light/60 hover:text-primary transition-colors"
                          >
                            {planItem.name === 'Pro' && billingCycle === 'monthly' 
                              ? 'Pay $12/month with PayPal' 
                              : planItem.name === 'Pro' && billingCycle === 'yearly'
                              ? 'Pay $120/year with PayPal'
                              : planItem.name === 'Team' && billingCycle === 'monthly'
                              ? 'Pay $29/month with PayPal'
                              : planItem.name === 'Team' && billingCycle === 'yearly'
                              ? 'Pay $290/year with PayPal'
                              : 'Pay with PayPal'
                            }
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-light/60">
              *Fair usage policy applies. Need more? 
              <a href="mailto:sales@coldmail.ai" className="text-primary hover:underline ml-1">
                Contact us
              </a>
            </p>
          </div>
        </div>
      </MotionSection>

      {/* FAQ */}
      <MotionSection className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Frequently <span className="gradient-text">asked questions</span>
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="gradient-border rounded-xl p-6"
              >
                <div className="gradient-border-inner p-6">
                  <h3 className="text-lg font-semibold mb-3">{faq.question}</h3>
                  <p className="text-light/70">{faq.answer}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </MotionSection>

      {/* Final CTA */}
      <MotionSection className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to <span className="gradient-text">supercharge</span> your outreach?
          </h2>
          <p className="text-xl text-light/80 mb-8">
            Join thousands of sales professionals already using ColdMail AI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handleStripeCheckout('pro')}
              className="btn-primary px-8 py-4 rounded-lg text-white font-medium text-lg"
            >
              Start Pro Trial
            </button>
            <a
              href="/app"
              className="px-8 py-4 border border-primary text-primary rounded-lg font-medium text-lg hover:bg-primary hover:text-white transition-colors"
            >
              Try Free First
            </a>
          </div>
          <p className="text-sm text-light/60 mt-4">
            30-day money-back guarantee • No setup fees • Cancel anytime
          </p>
        </div>
      </MotionSection>
    </motion.div>
  );
};

export default Pricing;